<script lang="ts" setup>
import { useBasketStore } from '@/store/basket/basket'
import { useUserStore } from '~/store/user/user'
// components
import BasketTitle from '@/components/Basket/basket-utils/BasketTitle.vue'
import BasketDiscountChoices from '@/components/Basket/basket-discount/BasketDiscountChoices.vue'
import BasketDiscountInput from '@/components/Basket/basket-discount/BasketDiscountInput.vue'
import BasketDiscountInfo from '@/components/Basket/basket-discount/BasketDiscountInfo.vue'

// types
import { SelectedDiscount } from '@/types/basket'
import { Success, UnprocessableEntityError } from '~/api/results'

const { $metrics } = useNuxtApp()

const basketStore = useBasketStore()
const userStore = useUserStore()

const selectedDiscount = ref<SelectedDiscount>(SelectedDiscount.PROMOCODE)
const confirmedDiscount = ref<SelectedDiscount | null>(null)
const inputValue = ref('')
const isButtonLoading = ref(false)

const bonusesCount = computed(() => basketStore.data.bonuses.totalAmount)

const basketDiscountErrors = computed(
  () =>
    basketStore.data.promocode.error || basketStore.data.bonuses.errorMessage,
)

const basketDiscountInfoVisible = computed(
  () =>
    (basketStore.data.promocode.value &&
      basketStore.data.promocode.valid &&
      !basketDiscountErrors.value) ||
    (basketStore.data.bonuses.toSpendAmount > 0 && !basketDiscountErrors.value),
)

const bonuses = computed(() => basketStore.data.bonuses)

const discountTitle = computed(
  () => basketStore.data.promocode.title || bonuses?.value.message,
)

const isAuthenticated = computed(() => userStore.isAuthenticated)

const setPromocode = async (setErrors: Function) => {
  isButtonLoading.value = true

  const result = await basketStore.promocodeApply({
    value: inputValue.value,
  })

  if (!(result instanceof Success)) {
    setErrors(result.data)
  }

  if (result instanceof Success) {
    confirmedDiscount.value = selectedDiscount.value

    $metrics?.eventSendGoal({ ym: 'use_ref' })
  }

  isButtonLoading.value = false
}

const setBonuses = async (value: number, setErrors: Function) => {
  isButtonLoading.value = true

  const result = await basketStore.applyBonuses({ amount: value })

  if (result instanceof UnprocessableEntityError) {
    setErrors({ bonuses: result.data.amount })
  }

  if (result instanceof Success) {
    // Определяем, это reset bonus или apply
    value === 0
      ? (confirmedDiscount.value = null)
      : (confirmedDiscount.value = selectedDiscount.value)
  }

  isButtonLoading.value = false
}

const resetPromocode = async () => {
  isButtonLoading.value = true

  await basketStore.promocodeReset()

  inputValue.value = ''

  isButtonLoading.value = false
  confirmedDiscount.value = null
}

const updateSelectedDiscount = (value: SelectedDiscount) => {
  inputValue.value = ''
  selectedDiscount.value = value
}

const updateModelValue = (value: string) => (inputValue.value = value)

const setDiscount = (setErrors: Function) =>
  selectedDiscount.value === SelectedDiscount.PROMOCODE
    ? setPromocode(setErrors)
    : setBonuses(+inputValue.value, setErrors)

const cancelDiscount = () =>
  selectedDiscount.value === SelectedDiscount.PROMOCODE
    ? resetPromocode()
    : setBonuses(0, () => {})

// Инициализируем начальный выбранный тип скидки
onMounted(() => {
  if (basketStore.data?.promocode?.value) {
    selectedDiscount.value = SelectedDiscount.PROMOCODE
    inputValue.value = basketStore.data?.promocode?.value
    confirmedDiscount.value = SelectedDiscount.PROMOCODE
  } else if (basketStore.data.bonuses.toSpendAmount) {
    selectedDiscount.value = SelectedDiscount.BONUSES
    inputValue.value = String(basketStore.data.bonuses.toSpendAmount)
    confirmedDiscount.value = SelectedDiscount.BONUSES
  }
})
</script>

<template>
  <section id="discount" class="discount">
    <basket-title class="discount__headline" title="Применение скидок" />

    <basket-discount-choices
      :selected-discount="selectedDiscount"
      :bonuses-count="bonusesCount"
      :confirmed-discount="confirmedDiscount"
      :spend-amount-bonuses="bonuses.toSpendAmount"
      :bonus-spend-message="bonuses.message"
      :is-authenticated="isAuthenticated"
      :basket-discount-info-visible="basketDiscountInfoVisible"
      @update-discount-choices="updateSelectedDiscount"
    />

    <div class="discount__controls">
      <basket-discount-input
        :model-value="inputValue"
        :selected-discount="selectedDiscount"
        :confirmed-discount="confirmedDiscount"
        :is-button-loading="isButtonLoading"
        :error="basketDiscountErrors"
        @set-discount="setDiscount"
        @cancel-discount="cancelDiscount"
        @update-model-value="updateModelValue"
      />
    </div>

    <transition class="discount__info" name="height">
      <basket-discount-info
        v-if="basketDiscountInfoVisible"
        :discount-title="discountTitle"
      />
    </transition>

    <div
      id="basket-discount-observable-target"
      class="basket-observable-target"
    />
  </section>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/animations.scss';

.discount {
  &__headline {
    margin-bottom: 20px;
  }

  &--error {
    border: 1px solid $danger;
  }

  &__controls {
    margin-top: 15px;
  }

  &__info {
    margin-top: 25px;
  }
}

:deep(.v-label) {
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  color: $text-black !important;
  opacity: 1 !important;
}

:deep(.v-icon) {
  opacity: 1 !important;
}
</style>
